import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { GOOGLE_PAY_SDK_URL } from '@/constants/nuvei';
import { loadScriptOnce } from '@/utils/nuvei';
import {
  buildDirectStripeGooglePayIsReadyToPayRequest,
  buildDirectStripeGooglePayPaymentDataRequest,
  mapDirectGooglePayBillingDetails,
  parseStripeTokenIdFromGooglePayPaymentData,
  resolveDirectGooglePayMerchantInfo,
  resolveGooglePayEnvironment,
  type DirectGooglePayPaymentData,
} from '@/utils/googlePayDirect';
import { getStripePaymentKeyBySelectedPaymentMethod } from '@/utils/stripe';
// Shared Adyen/Google Pay `transactionInfo` amount + itemized-tax builders —
// the exact functions the working Adyen direct integration sends to the same
// Google sheet (see getGooglePayComponentOptions).
import { getPaymentRequestAmount } from '@/utils/pricing';
import { buildGooglePayTaxTransactionInfo } from '@/utils/adyen';
import { shouldCollectZipCodeForTaxCalculation, shouldUseWalletDerivedZipTax } from '@/utils/tax';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { logError } from '@/utils/logger';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { useGooglePayShippingZipTax } from '@/hooks/useGooglePayShippingZipTax';
import { useLatestRef } from '@/hooks/useLatestRef';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { GooglePayButtonPlaceholder } from '@/components/Wallets/WalletButton/GooglePayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';
import type { StripeDirectGooglePayParams } from '@/types/paymentHandlers';
import type { ExcludeOtherPaymentMethod } from '@/types/common';

/**
 * Stripe-routed Google Pay rendered as a DIRECT pay.js integration
 * (`checkout_stripe_direct_google_pay`).
 *
 * Stripe's Express Checkout Element relays the Google Pay approval through a
 * `returnUrl` on `b.stripecdn.com` on WebKit, and that handshake fails at the
 * approval step on iOS (`OR_BIBED_15`). Here OUR page calls
 * `loadPaymentData` — Google keeps the `window.opener` handback it verifies
 * (the same architecture as the working Adyen/Nuvei Google Pay forms) — and
 * the returned Stripe Token rides the existing Stripe finalize path, so
 * processing/sub-MIDs are unchanged.
 */

// pay.js client surface (window.google is declared globally in NuveiGooglePayForm).
type DirectGooglePaymentsClient = {
  isReadyToPay: (request: unknown) => Promise<{ result: boolean }>;
  loadPaymentData: (request: unknown) => Promise<unknown>;
  createButton: (options: {
    onClick: () => void;
    buttonType?: string;
    buttonColor?: string;
    buttonSizeMode?: 'static' | 'fill';
    buttonRadius?: number;
    buttonBorderType?: 'no_border' | 'default_border';
  }) => HTMLElement;
};

export const GooglePayDirectStripeForm = ({
  onInvalidTaxClick,
  onReady,
  isExpress,
}: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const {
    paymentKeys,
    paymentMethodsSettings,
    pricing,
    customer,
    addressDetails,
    featureFlags,
    sessionStartedWithoutValidTax,
    actions: { runPayment, setError, setIsExpressPaymentInProcess },
  } = context;

  const providerSettings = useMemo(
    () =>
      paymentMethodsSettings.find(
        (pm) => pm.paymentMethodName === PaymentMethodEnum.GOOGLE_PAY && pm.provider === ProviderEnum.STRIPE
      ),
    [paymentMethodsSettings]
  );
  const publishableKey =
    getStripePaymentKeyBySelectedPaymentMethod(
      paymentMethodsSettings,
      PaymentMethodEnum.GOOGLE_PAY as ExcludeOtherPaymentMethod,
      paymentKeys
    ) || '';
  const merchantInfo = useMemo(() => resolveDirectGooglePayMerchantInfo(providerSettings), [providerSettings]);
  const environment = resolveGooglePayEnvironment(publishableKey);

  // ACDEV-25067 (`checkout_wallet_zip_tax`) + ACDEV-27914: US/CA + (FF ON OR
  // session started without a resolved tax) collect the buyer's ZIP inside
  // the Google sheet (shipping address callback → in-sheet tax repricing)
  // instead of the on-page InputTaxComponent — same flow as the Adyen direct
  // integration and Stripe ECE. The hook returns NO callback when the
  // composite gate is off, and the request builder then omits the
  // shipping-address intents, keeping the sheet identical to the FF-OFF +
  // resolved-boot-tax path.
  const collectZipForTax = shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax);
  const { onPaymentDataChanged, capturedWalletTaxRef, applyWalletTaxToCheckout } = useGooglePayShippingZipTax({
    provider: 'stripe',
  });

  // Express instance holds the wallet sub-MID pk — the token→PaymentMethod
  // conversion in the payment handler runs against it.
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: PaymentMethodEnum.GOOGLE_PAY,
    isExpress: true,
  });

  const [state, setState] = useState<WalletReadyState>('loading');
  const [paymentsClient, setPaymentsClient] = useState<DirectGooglePaymentsClient | null>(null);
  const buttonHostRef = useRef<HTMLDivElement | null>(null);
  const isPayingRef = useRef(false);
  const mountTimeRef = useRef(Date.now());
  const reportWalletAvailability = useExpressWalletFallback();
  // Last settled availability. `null` = not reported yet. A late DOWNGRADE
  // (pay.js reported available, then Stripe.js terminally failed) must still
  // go through — otherwise `useExpressWalletFallback` never runs and the
  // PREFERRED template strands the user on a CTA that unmounted. Upgrades
  // after a negative report stay blocked (the form already collapsed).
  const availabilityRef = useRef<boolean | null>(null);

  const contextRef = useLatestRef(context);
  const stripeRef = useLatestRef(stripe);
  const onInvalidTaxClickRef = useLatestRef(onInvalidTaxClick);
  const onReadyRef = useLatestRef(onReady);
  const runPaymentRef = useLatestRef(runPayment);
  const setErrorRef = useLatestRef(setError);
  const setIsExpressPaymentInProcessRef = useLatestRef(setIsExpressPaymentInProcess);
  const paymentsClientRef = useLatestRef(paymentsClient);
  const pricingRef = useLatestRef(pricing);
  const customerRef = useLatestRef(customer);
  const addressDetailsRef = useLatestRef(addressDetails);
  const merchantInfoRef = useLatestRef(merchantInfo);
  const publishableKeyRef = useLatestRef(publishableKey);
  const collectZipForTaxRef = useLatestRef(collectZipForTax);
  // Callback identity changes on pricing updates (in-sheet repricing writes a
  // new breakdown) — route through a ref so the PaymentsClient, which
  // registers callbacks at CONSTRUCTION, never has to be re-created mid-sheet.
  const onPaymentDataChangedRef = useLatestRef(onPaymentDataChanged);
  const applyWalletTaxToCheckoutRef = useLatestRef(applyWalletTaxToCheckout);

  const settleAvailability = useCallback(
    (isAvailable: boolean, reason?: string) => {
      if (availabilityRef.current === isAvailable || availabilityRef.current === false) return;
      availabilityRef.current = isAvailable;
      reportWalletAvailability(isAvailable, {
        wallet: 'google_pay',
        provider: ProviderEnum.STRIPE,
        reason,
        loadTimeMs: Date.now() - mountTimeRef.current,
      });
      onReadyRef.current?.(isAvailable);
    },
    [onReadyRef, reportWalletAvailability]
  );

  useEffect(() => {
    if (!providerSettings || !publishableKey) {
      if (!providerSettings) return;
      logError('Direct Stripe Google Pay missing publishable key', { paymentMethodId: providerSettings.id });
      setState('unavailable');
      settleAvailability(false, 'init_error');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        await loadScriptOnce(GOOGLE_PAY_SDK_URL, 'google-pay-sdk');
        const PaymentsClient = window.google?.payments?.api?.PaymentsClient;
        if (!PaymentsClient) {
          throw new Error('Google Pay SDK unavailable');
        }

        const client = new PaymentsClient({
          environment,
          // Registered at construction (pay.js contract). The wrapper reads
          // the ref at fire time, so in-sheet repricing callbacks always see
          // the latest pricing without re-creating the client.
          ...(collectZipForTax
            ? {
                // Google: "If you set up Dynamic Price Updates ... be sure to
                // add MerchantInfo and PaymentDataCallbacks fields."
                merchantInfo: merchantInfoRef.current,
                paymentDataCallbacks: {
                  onPaymentDataChanged: (intermediatePaymentData: unknown) =>
                    onPaymentDataChangedRef.current
                      ? (onPaymentDataChangedRef.current(
                          intermediatePaymentData as Parameters<NonNullable<typeof onPaymentDataChangedRef.current>>[0]
                        ) as Promise<unknown>)
                      : Promise.resolve({}),
                  // Google mandates this callback whenever SHIPPING_ADDRESS
                  // intents are used. Approve immediately so the sheet closes
                  // and `loadPaymentData` resolves with the final paymentData;
                  // token conversion + finalize then run post-sheet — the
                  // exact same architecture as the FF-OFF path and the ECE
                  // confirm flow, so failures surface on-page identically.
                  onPaymentAuthorized: () => Promise.resolve({ transactionState: 'SUCCESS' as const }),
                },
              }
            : {}),
        }) as DirectGooglePaymentsClient;
        const ready = await client.isReadyToPay(buildDirectStripeGooglePayIsReadyToPayRequest());
        if (cancelled) return;

        if (!ready.result) {
          setState('unavailable');
          settleAvailability(false, 'device_unsupported');
          return;
        }

        setPaymentsClient(client);
        setState('ready');
        settleAvailability(true);
      } catch (error) {
        logError('Failed to init direct Stripe Google Pay', error);
        if (!cancelled) {
          setState('unavailable');
          settleAvailability(false, 'load_error');
        }
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, [
    providerSettings,
    publishableKey,
    environment,
    collectZipForTax,
    merchantInfoRef,
    onPaymentDataChangedRef,
    settleAvailability,
  ]);

  const onPay = useCallback(() => {
    const activeClient = paymentsClientRef.current;
    if (!activeClient || isPayingRef.current) {
      return;
    }

    // `checkout_wallet_zip_tax` ON: the sheet collects the ZIP (shipping
    // address callback) and reprices in-sheet — don't block on the on-page
    // input, mirroring the ECE/Adyen wallet-tax paths.
    const walletCollectsZip = collectZipForTaxRef.current;
    const needsZip =
      !walletCollectsZip &&
      shouldCollectZipCodeForTaxCalculation(
        customerRef.current,
        addressDetailsRef.current?.zipCode ?? customerRef.current.address?.zipCode,
        pricingRef.current.priceBreakdown.isTaxValid
      );
    if (needsZip) {
      onInvalidTaxClickRef.current?.();
      return;
    }

    const fallbackCountryCode = (
      customerRef.current.geoLocator?.countryCode2 ||
      customerRef.current.taxCountry ||
      'US'
    ).toUpperCase();

    const paymentDataRequest = buildDirectStripeGooglePayPaymentDataRequest({
      publishableKey: publishableKeyRef.current,
      merchantInfo: merchantInfoRef.current,
      // Same amount helper Adyen's Google Pay sends for `transactionInfo.totalPrice`.
      totalPrice: getPaymentRequestAmount(pricingRef.current.priceBreakdown.priceTotal, pricingRef.current),
      currencyCode: pricingRef.current.currencyCode,
      countryCode: fallbackCountryCode,
      walletTaxTransactionInfo: walletCollectsZip
        ? buildGooglePayTaxTransactionInfo(pricingRef.current.priceBreakdown, pricingRef.current, fallbackCountryCode)
        : undefined,
    });

    isPayingRef.current = true;
    trackAndNotifyPaymentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });

    // Must start synchronously from the click stack — awaiting anything first
    // drops the user gesture Google requires to open the sheet.
    void activeClient
      .loadPaymentData(paymentDataRequest)
      .then(async (rawPaymentData) => {
        const paymentData = rawPaymentData as DirectGooglePayPaymentData;
        const googlePayTokenId = parseStripeTokenIdFromGooglePayPaymentData(paymentData);
        if (!googlePayTokenId) {
          throw new Error('Google Pay returned no Stripe token');
        }

        notifyPaymentIntentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.GOOGLE_PAY });

        // Sheet is closed — show the express loader while token→PaymentMethod
        // conversion + finalize + order polling run.
        setIsExpressPaymentInProcessRef.current(true);

        const billingDetails = mapDirectGooglePayBillingDetails(paymentData, {
          email: customerRef.current.email || '',
          fallbackCountry: fallbackCountryCode,
        });

        const params: StripeDirectGooglePayParams = {
          googlePayTokenId,
          billingDetails,
          // ACDEV-25067: only present when the in-sheet wallet ZIP resolved a
          // valid taxed total — the BE recomputes the same tax for the charge.
          // Undefined otherwise, so the wire format matches the FF-OFF path.
          zipCode: capturedWalletTaxRef.current?.zipCode,
          // This form's own express instance (wallet sub-MID pk) — another
          // express form on the page may have overwritten the shared slot.
          stripe: stripeRef.current ?? undefined,
        };
        try {
          await runPaymentRef.current(params, PaymentMethodEnum.GOOGLE_PAY);
        } finally {
          isPayingRef.current = false;
        }
      })
      .catch((error: { statusCode?: string } | Error) => {
        isPayingRef.current = false;
        // Sheet is torn down — the SAFE window to flush wallet-derived
        // tax/ZIP into checkout context (no-op when nothing was captured).
        // Mirrors ECE's `onCancel={applyWalletTaxToCheckout}` + confirm-catch.
        applyWalletTaxToCheckoutRef.current();
        const statusCode = typeof error === 'object' && error && 'statusCode' in error ? error.statusCode : undefined;
        if (statusCode === 'CANCELED' || statusCode === 'USER_CANCELED') {
          setIsExpressPaymentInProcessRef.current(false);
          return;
        }
        logError('Direct Stripe Google Pay payment failed', error);
        setErrorRef.current('Google Pay payment failed');
        setIsExpressPaymentInProcessRef.current(false);
      });
  }, [
    addressDetailsRef,
    applyWalletTaxToCheckoutRef,
    capturedWalletTaxRef,
    collectZipForTaxRef,
    contextRef,
    customerRef,
    merchantInfoRef,
    onInvalidTaxClickRef,
    paymentsClientRef,
    pricingRef,
    publishableKeyRef,
    runPaymentRef,
    setErrorRef,
    setIsExpressPaymentInProcessRef,
    stripeRef,
  ]);

  const onPayRef = useLatestRef(onPay);

  useEffect(() => {
    if (state !== 'ready' || !paymentsClient || !buttonHostRef.current) {
      return;
    }
    buttonHostRef.current.innerHTML = '';
    const button = paymentsClient.createButton({
      onClick: () => {
        onPayRef.current();
      },
      buttonType: 'plain',
      buttonColor: 'black',
      buttonSizeMode: 'fill',
      buttonRadius: 8,
      buttonBorderType: 'no_border',
    });
    button.style.width = '100%';
    button.style.height = '100%';
    buttonHostRef.current.appendChild(button);
  }, [onPayRef, paymentsClient, state]);

  // Stripe.js is required to convert the token at pay time — keep the button
  // non-interactive until the express instance settled.
  const isPaymentReady = state === 'ready' && Boolean(stripe) && !isStripeLoading;

  useEffect(() => {
    const button = buttonHostRef.current?.querySelector<HTMLButtonElement>('button');
    if (button) {
      button.disabled = !isPaymentReady;
    }
  }, [isPaymentReady]);

  // Stripe.js terminally failed (null resolve / missing key): the token could
  // never be converted, so collapse instead of presenting a dead button.
  useEffect(() => {
    if (isStripeLoading || stripe) return;
    setState('unavailable');
    settleAvailability(false, 'init_error');
  }, [isStripeLoading, stripe, settleAvailability]);

  if (state === 'unavailable') return <PaymentMethodNotSupported isExpress={isExpress} />;

  return (
    <WalletButtonShell
      state={state}
      placeholder={<GooglePayButtonPlaceholder />}
      testId="wallet-btn-stripe-google-direct"
      usePlaceholderAsVisual
    >
      <div
        ref={buttonHostRef}
        className={`w-full h-12 min-h-12 rounded-lg overflow-hidden ${isPaymentReady ? '' : 'pointer-events-none'}`}
        data-testid="stripe-direct-google-pay-button"
        aria-disabled={!isPaymentReady || undefined}
      />
    </WalletButtonShell>
  );
};
